// PlanComponents/ActualResultsInput.js
import React, { useState, useEffect } from 'react';
import { Typography, Box, TextField, Button, Grid, Snackbar, Alert } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';

/**
 * 実績入力フォーム
 */
export default function ActualResultsInput({ planId, actuals, onSave, isSaving }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    actual_start_date: '',
    actual_end_date: '',
    actual_budget_used: '',
    actual_applicants: '',
    actual_hires: '',
    current_applicants: '',
    current_hires: '',
    actual_budget_media: '',
    actual_budget_agency: '',
    actual_budget_others: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // actuals が変わったらフォームを更新
  useEffect(() => {
    if (actuals) {
      setFormData({
        actual_start_date: actuals.actual_start_date || '',
        actual_end_date: actuals.actual_end_date || '',
        actual_budget_used: actuals.actual_budget_used !== null && actuals.actual_budget_used !== undefined ? String(actuals.actual_budget_used) : '',
        actual_applicants: actuals.actual_applicants !== null && actuals.actual_applicants !== undefined ? String(actuals.actual_applicants) : '',
        actual_hires: actuals.actual_hires !== null && actuals.actual_hires !== undefined ? String(actuals.actual_hires) : '',
        current_applicants: actuals.current_applicants !== null && actuals.current_applicants !== undefined ? String(actuals.current_applicants) : '',
        current_hires: actuals.current_hires !== null && actuals.current_hires !== undefined ? String(actuals.current_hires) : '',
        actual_budget_media: actuals.actual_budget_media !== null && actuals.actual_budget_media !== undefined ? String(actuals.actual_budget_media) : '',
        actual_budget_agency: actuals.actual_budget_agency !== null && actuals.actual_budget_agency !== undefined ? String(actuals.actual_budget_agency) : '',
        actual_budget_others: actuals.actual_budget_others !== null && actuals.actual_budget_others !== undefined ? String(actuals.actual_budget_others) : '',
      });
    }
  }, [actuals]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    // 空文字列をnullに変換、数値フィールドはintに変換
    const payload = {};
    const dateFields = ['actual_start_date', 'actual_end_date'];
    const intFields = ['actual_budget_used', 'actual_applicants', 'actual_hires',
      'current_applicants', 'current_hires',
      'actual_budget_media', 'actual_budget_agency', 'actual_budget_others'];

    dateFields.forEach(f => {
      if (formData[f]) payload[f] = formData[f];
    });

    intFields.forEach(f => {
      if (formData[f] !== '' && formData[f] !== null && formData[f] !== undefined) {
        const val = parseInt(formData[f], 10);
        if (!isNaN(val)) payload[f] = val;
      }
    });

    try {
      await onSave(payload);
      setSnackbar({ open: true, message: '実績データを保存しました', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: '保存に失敗しました', severity: 'error' });
    }
  };

  return (
    <CardItem title="実績入力">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* 日付 */}
        <Box sx={{ p: 2, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: '#f37335' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
              採用期間（実績）
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="開始日"
                type="date"
                size="small"
                fullWidth
                value={formData.actual_start_date}
                onChange={handleChange('actual_start_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="終了日"
                type="date"
                size="small"
                fullWidth
                value={formData.actual_end_date}
                onChange={handleChange('actual_end_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 応募者・採用人数 */}
        <Box sx={{ p: 2, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
            <PeopleIcon sx={{ fontSize: 16, color: '#f37335' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
              採用実績
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="現在の応募者数"
                type="number"
                size="small"
                fullWidth
                value={formData.current_applicants}
                onChange={handleChange('current_applicants')}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="現在の採用人数"
                type="number"
                size="small"
                fullWidth
                value={formData.current_hires}
                onChange={handleChange('current_hires')}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="最終応募者数"
                type="number"
                size="small"
                fullWidth
                value={formData.actual_applicants}
                onChange={handleChange('actual_applicants')}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="最終採用人数"
                type="number"
                size="small"
                fullWidth
                value={formData.actual_hires}
                onChange={handleChange('actual_hires')}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 予算実績 */}
        <Box sx={{ p: 2, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 16, color: '#f37335' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
              予算実績（円）
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="使用した予算合計（円）"
                type="number"
                size="small"
                fullWidth
                value={formData.actual_budget_used}
                onChange={handleChange('actual_budget_used')}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={4}>
              <TextField
                label="媒体費（円）"
                type="number"
                size="small"
                fullWidth
                value={formData.actual_budget_media}
                onChange={handleChange('actual_budget_media')}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="人材紹介費（円）"
                type="number"
                size="small"
                fullWidth
                value={formData.actual_budget_agency}
                onChange={handleChange('actual_budget_agency')}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="その他（円）"
                type="number"
                size="small"
                fullWidth
                value={formData.actual_budget_others}
                onChange={handleChange('actual_budget_others')}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 保存ボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #f37335, #ff9966)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #e06530, #f08850)',
              }
            }}
          >
            {isSaving ? '保存中...' : '実績を保存'}
          </Button>
        </Box>

        <Typography variant="caption" sx={{ color: textColors(theme).muted, fontStyle: 'italic' }}>
          全ての項目は任意入力です。入力された項目のみ更新されます。
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </CardItem>
  );
}
